import { Injectable } from '@angular/core';
import {AppConfig} from '../app.config';
import {hoslog} from '../app.constants';
import {UserDeviceService} from '../api-client/api/user-device.service';

// | Platform    | device_type |
// | ----------- | ------------ |
// | iOS         | 1            |
// | Android     | 3            |
// | Web Chrome  | 11           |
// | Web Safari  | 10           |
// | Web Firefox | 12           |
// | macOS       | 13           |
// | Windows     | 14           |

declare const Pushwoosh: any;

@Injectable({
  providedIn: 'root',
})
export class PushwooshService {

  constructor(private userDeviceService: UserDeviceService){}

  private initialized = false;

  public initPushwoosh(): void {
    if (!AppConfig.pushwooshEnabled) {
      hoslog('[Pushwoosh] Disabled via config. Skipping initialization.');
      return;
    }

    // Check browser API compatibility
    if (!('serviceWorker' in navigator && 'PushManager' in window && 'permissions' in navigator && 'Notification' in window)) {
      hoslog('[Pushwoosh] Push notifications are not supported in this browser. Skipping initialization.');
      return;
    }

    if (this.initialized) {
      hoslog('[Pushwoosh] Already initialized.');
      return;
    }

    // Wait until the Pushwoosh global object is available
    if (typeof Pushwoosh === 'undefined') {
      const retryInterval = setInterval(() => {
        if (typeof Pushwoosh !== 'undefined') {
          clearInterval(retryInterval);
          this.initPushwoosh(); // retry initialization
        }
      }, 100);
      return;
    }

    console.log('[Pushwoosh] Initializing...');
    console.log('AppCode:', AppConfig.pushwooshApplicationCode);

    // Initialize Pushwoosh SDK
    Pushwoosh.push(['init', {
      applicationCode: AppConfig.pushwooshApplicationCode,
      apiToken: AppConfig.pushwooshDeviceApiToken,
      serviceWorkerUrl: 'pushwoosh-service-worker.js',
      autoSubscribe: false, // Disable automatic subscription
      logLevel: 'debug',
    }]);

    // Wait until SDK is fully ready before marking as initialized
    Pushwoosh.push((api) => {
      if (api && typeof api.subscribe === 'function') {
        this.initialized = true;
        console.log('[Pushwoosh] SDK initialized and ready.');
      } else {
        console.warn('[Pushwoosh] SDK initialized, but some API methods are not available.');
      }
    });
  }

  public subscribe(): void {
    if (!AppConfig.pushwooshEnabled) return;

    if (typeof Pushwoosh === 'undefined') {
      console.warn('[PushwooshService] SDK not loaded yet. Cannot subscribe.');
      return;
    }

    try {
      Pushwoosh.subscribe();
      console.log('[PushwooshService] subscribe() called.');
    } catch (err: any) {
      console.warn('[PushwooshService] subscribe() failed:', err.message || err);
    }
  }

  setUserId(userId: string) {
    if (AppConfig.pushwooshEnabled && typeof Pushwoosh !== 'undefined') {
      Pushwoosh.push(['setTags', { user_id: userId }]);
      console.log('[Pushwoosh] setTags pushed for user_id:', userId);
    } else {
      console.warn('[Pushwoosh] SDK not available or disabled');
    }
  }

  setTags(tags: { [key: string]: any }) {
    if(AppConfig.pushwooshEnabled) {
      Pushwoosh.push(() => {
        Pushwoosh.setTags(tags);
      });
    }
  }

  async registerUserToPushwoosh(userId: number): Promise<void> {
    // const platform = 11; // es. Chrome Web
    const platform = this.getPlatformCode();
    const isMobile = /Mobi|Android/i.test(navigator.userAgent);
    const hosPlatform = isMobile ? 'mobile' : 'desktop';

    console.log('[PushwooshService] In registerUserToPushwoosh...');

    // Trigger the browser permission prompt if not already granted.
    // subscribe() is a no-op if already subscribed.
    this.subscribe();

    const hwid = await this.getHWID();
    const pushToken = await this.getPushToken();

    if(!hwid) {
      console.warn('[PushwooshService] HWID missing. Skipping registration.');
      return;
    }

    if (!pushToken) {
      console.warn('[PushwooshService] PushToken missing. Skipping registration.');
      return;
    }

    this.userDeviceService.registerDevice(hwid, platform, pushToken, hosPlatform).subscribe({
      next: () => {
        console.log(`[PushwooshService] Successfully registered device: userId=${userId}, hwid=${hwid}`);
      },
      error: err => {
        console.error('[PushwooshService] Failed to register device via userDeviceService:', err);
      }
    });
  }

  async getHWID(): Promise<string | null> {
    console.log('[PushwooshService] Requesting HWID...');

    try {
      if (window['Pushwoosh'] && typeof window['Pushwoosh'].getHWID === 'function') {
        console.log('[PushwooshService] Using window.Pushwoosh...');
        const result = await window['Pushwoosh'].getHWID();
        if (result) {
          console.log('[PushwooshService] HWID:', result);
          return result;
        }
      }

      console.warn('[PushwooshService] HWID not available. SDK may not be ready.');
      return null;

    } catch (err: any) {
      console.error('[PushwooshService] Error getting HWID:', err.message || err);
      return null;
    }
  }

  async getPushToken(): Promise<string | null> {
    // If the user hasn't granted notification permission, there's no push token
    // to retrieve. Avoids a destructuring error inside the Pushwoosh SDK on
    // Safari and Firefox where the subscription object is null.
    if (typeof Notification !== 'undefined' && Notification.permission !== 'granted') {
      console.log('[PushwooshService] Notification permission not granted — skipping getPushToken.');
      return null;
    }

    try {
      if (window['Pushwoosh']?.getPushToken) {
        return await window['Pushwoosh'].getPushToken();
      }
      return null;
    } catch (err: any) {
      console.warn('[PushwooshService] Error getting PushToken:', err.message || err);
      return null;
    }
  }

  private getPlatformCode(): number {
    const userAgent = navigator.userAgent;
    const platform = navigator.platform || '';

    if (/Chrome/.test(userAgent) && !/Edge|Edg|OPR/.test(userAgent)) return 11;
    if (/Safari/.test(userAgent) && !/Chrome/.test(userAgent)) return 10;
    if (/Firefox/.test(userAgent)) return 12;
    if (/Mac/.test(platform)) return 13;
    if (/Win/.test(platform)) return 14;
    return 11;
  }
}
